/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, ChangeDetectionStrategy, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	DEFAULT_POPUP_CANCEL_BUTTON,
	DEFAULT_POPUP_SAVE_BUTTON,
	GENERAL_NONE,
	MULTI_SELECT_MENU_EDIT_LOCATIONS,
	PROJECT_ITEM_LOCATION_SINGULAR,
	TICKET_EDIT_LOCATIONS_DIALOG_TEXT,
	UICONTROL_TEXT_CANCEL,
	UICONTROL_TEXT_SAVE,
} from '@touchplan/strings';
import { Ticket } from '@touchplan/common';
import { Location } from '@touchplan/common';
import { PlanState } from 'src/app/services/plan-state.service';
import { UntypedFormControl } from '@angular/forms';
import { map, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IButtonPanelEventData } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { TicketEditService } from '../../ticket-edit/ticket-edit.service';

@Component({
	selector: 'ticket-edit-locations',
	templateUrl: './ticket-edit-locations.component.html',
	styleUrls: ['./ticket-edit-locations.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketEditLocationsComponent implements OnInit, OnDestroy {
	constructor(
		@Inject(MAT_DIALOG_DATA)
		public receivedData: { data: Map<string, Ticket>; isFloatingTicket$: Observable<boolean> },
		public dialog: MatDialogRef<TicketEditLocationsComponent>,
		public planState: PlanState,
		private ticketEditService: TicketEditService
	) {}

	public shutdownSubject$: Subject<boolean> = new Subject();
	public templateStringList = {
		DEFAULT_POPUP_CANCEL_BUTTON,
		DEFAULT_POPUP_SAVE_BUTTON,
		MULTI_SELECT_MENU_EDIT_LOCATIONS,
		PROJECT_ITEM_LOCATION_SINGULAR,
		TICKET_EDIT_LOCATIONS_DIALOG_TEXT,
		UICONTROL_TEXT_CANCEL,
		UICONTROL_TEXT_SAVE,
	};

	public data: Map<string, Ticket>;
	public selectedLocation: UntypedFormControl = new UntypedFormControl('');
	public locations: BehaviorSubject<Map<string, Location>>;
	public locationList$: Observable<Array<{ label: string; value: string }>>;
	public tickets: TicketList;

	public ngOnInit() {
		this.data = this.receivedData.data;
		this.locations = this.planState.locationsApi.data.list$;

		this.receivedData.isFloatingTicket$.pipe(takeUntil(this.shutdownSubject$)).subscribe((value) => {
			if (value) {
				this.tickets = this.planState.floatingTickets;
			} else {
				this.tickets = this.planState.tickets;
			}
		});

		this.updateLocationValueIfAllSelectedAreSame();
		this.selectedLocation.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((newLocation) => {
			this.data.forEach((key) => {
				if (key.$id) {
					this.planState.actions.updateTicketLiveData(key.$id, { locationId: newLocation }, this.tickets);
				}
			});
		});

		this.locationList$ = this.locations.pipe(
			map((list) => {
				const selectionList = this.generateList(list);
				return selectionList;
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	public handleItemEvent(emittedData: IButtonPanelEventData) {
		if (emittedData.type === 'Cancel') {
			this.cancel();
		}

		if (emittedData.type === 'Save') {
			this.save();
		}
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}

	private updateLocationValueIfAllSelectedAreSame() {
		const locationIds = [];
		this.data.forEach((key) => {
			locationIds.push(key.view.locationId);
		});

		if (locationIds.every((val, i, arr) => val === arr[0])) {
			this.selectedLocation.setValue(locationIds[0]);
		}
	}

	private save() {
		this.planState.actions.persistTicketData(this.data, this.tickets);
		this.planState.actions.changeTicketViewToDefault(this.data, this.tickets);
		this.ticketEditService.closeEditAndCheckDirty$.next(true); // reuse the same subject to trigger edit undo after state
		this.dialog.close();
	}

	private cancel() {
		this.planState.actions.changeTicketViewToDefault(this.data, this.tickets);
		this.dialog.close();
	}

	private generateList(list: Map<string, Location>) {
		const internalList = [{ label: GENERAL_NONE, value: 'none' }];

		const sortedLocations = new Map(
			[...list.entries()].sort((a, b) => {
				const firstLocationName: string = a[1].view.name.toLowerCase();
				const secondLocationName: string = b[1].view.name.toLowerCase();
				return firstLocationName.localeCompare(secondLocationName);
			})
		);

		sortedLocations.forEach((thing, id: string) => {
			internalList.push({
				label: thing.view.name || '',
				value: id,
			});
		});
		return internalList;
	}
}
